import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { api, formatApiError } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LockKey } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError(t("passwords_no_match")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success(data.message);
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F0FDF4]">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-emerald-100 p-8">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <img src="/logo.png" className="h-10 w-10 rounded-lg object-contain" alt="logo" />
          <span className="font-display font-extrabold text-emerald-900 text-lg">{t("brand")}</span>
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <LockKey size={22} weight="fill" className="text-emerald-600" />
          <h1 className="font-display text-2xl font-bold text-emerald-900">{t("reset_password")}</h1>
        </div>
        {!token ? (
          <p className="text-red-600 text-sm" data-testid="reset-no-token">{t("invalid_link")}</p>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            {error && <div data-testid="reset-error" className="mb-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>}
            <Label className="text-emerald-900 font-semibold">{t("new_password")}</Label>
            <Input data-testid="reset-pw-input" type="password" className="mt-1.5 h-11" value={pw} onChange={(e) => setPw(e.target.value)} required />
            <Label className="text-emerald-900 font-semibold mt-4 block">{t("confirm_password")}</Label>
            <Input data-testid="reset-pw2-input" type="password" className="mt-1.5 h-11" value={pw2} onChange={(e) => setPw2(e.target.value)} required />
            <Button data-testid="reset-submit-btn" disabled={loading} className="w-full mt-6 h-11 bg-emerald-800 hover:bg-emerald-900">
              {loading ? t("loading") : t("reset_password")}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
